import {
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  Spinner,
} from '@heroui/react';
import { type ReactNode, Fragment } from 'react';

import { EmptyState, EmptyStateProps } from '../EmptyState';

export interface LoadingTableColumn {
  /** Label for the column header */
  label: string;
  /** CSS class names for responsive visibility (e.g., "hidden md:table-cell") */
  className?: string;
  /** Width of the column */
  width?: number;
  /** Custom render function for the column header */
  headerRender?: () => ReactNode;
}

export interface LoadingTableProps<T = unknown> {
  /** Configuration for table columns */
  columns: LoadingTableColumn[];
  /** Number of skeleton rows to display (default: 5) */
  rowCount?: number;
  /** Aria label for the table */
  ariaLabel?: string;
  /** Custom className for the Card wrapper */
  className?: string;
  /** Loading message to display (default: "Loading...") */
  loadingMessage?: string;
  /** Show loading text above the table (default: true) */
  showLoadingText?: boolean;
  /** Whether the table is in a loading state */
  isLoading?: boolean;
  /** Whether the table has no data */
  isEmpty?: boolean;
  /** Empty state configuration */
  emptyState?: Omit<EmptyStateProps, 'showCard'>;
  /** Data to render in the table */
  data?: T[];
  /** Function to render a table row */
  renderRow?: (item: T, index: number) => ReactNode;
}

export const LoadingTable = <T = unknown,>({
  columns,
  rowCount = 5,
  ariaLabel = 'Loading table',
  className,
  loadingMessage = 'Loading...',
  showLoadingText = true,
  isLoading = false,
  isEmpty = false,
  emptyState,
  data = [],
  renderRow,
}: LoadingTableProps<T>) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          {showLoadingText && (
            <div className="flex items-center justify-center gap-3 p-6 border-b border-divider bg-default-50/50">
              <Spinner size="sm" color="primary" />
              <span className="text-default-600 font-medium">
                {loadingMessage}
              </span>
            </div>
          )}
          <Table
            aria-label={ariaLabel}
            classNames={{
              th: 'bg-default-100 text-default-700 border-b border-divider font-semibold',
              td: 'border-b border-divider',
            }}
            selectionMode="none"
          >
            <TableHeader>
              {columns.map((column, index) => (
                <TableColumn
                  key={index}
                  className={column.className}
                  width={column.width}
                >
                  {column.headerRender ? column.headerRender() : column.label}
                </TableColumn>
              ))}
            </TableHeader>
            <TableBody>
              {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column, colIndex) => {
                    const renderSkeletonContent = () => {
                      // Vary skeleton widths to make it more realistic
                      const textWidths = [
                        'w-16',
                        'w-20',
                        'w-24',
                        'w-28',
                        'w-32',
                        'w-36',
                      ];
                      const randomWidth =
                        textWidths[(rowIndex + colIndex) % textWidths.length];

                      if (colIndex === 0) {
                        // First column with avatar-like skeleton (common pattern)
                        return (
                          <div className="flex items-center gap-3 py-1">
                            <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <Skeleton className={`h-4 ${randomWidth} mb-2`} />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        );
                      }

                      if (colIndex === columns.length - 1) {
                        // Last column with action-like skeletons
                        return (
                          <div className="flex gap-2 justify-end">
                            <Skeleton className="h-8 w-8 rounded" />
                            <Skeleton className="h-8 w-8 rounded" />
                          </div>
                        );
                      }

                      // Middle columns with varied text-like skeletons
                      const isLongText = (rowIndex + colIndex) % 3 === 0;
                      return (
                        <div className="py-2">
                          <Skeleton
                            className={`h-4 ${
                              isLongText ? 'w-32' : randomWidth
                            }`}
                          />
                          {isLongText && <Skeleton className="h-3 w-16 mt-1" />}
                        </div>
                      );
                    };

                    return (
                      <TableCell key={colIndex} className={column.className}>
                        {renderSkeletonContent()}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      );
    }

    if (isEmpty && emptyState) {
      return <EmptyState {...emptyState} showCard={false} />;
    }

    if (data.length > 0 && renderRow) {
      return (
        <Table
          aria-label={ariaLabel}
          classNames={{
            th: 'bg-default-100 text-default-700 border-b border-divider font-semibold',
            td: 'border-b border-divider',
          }}
          selectionMode="none"
        >
          <TableHeader>
            {columns.map((column, index) => (
              <TableColumn
                key={index}
                className={column.className}
                width={column.width}
              >
                {column.headerRender ? column.headerRender() : column.label}
              </TableColumn>
            ))}
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <Fragment key={index}>{renderRow(item, index)}</Fragment>
            ))}
          </TableBody>
        </Table>
      );
    }

    return null;
  };

  return (
    <Card className={className}>
      <CardBody className="p-0">{renderContent()}</CardBody>
    </Card>
  );
};
